import { lazy } from "react";
import type { ComponentType, LazyExoticComponent } from "react";

const RELOAD_KEY = "chunk_reload_ts";
const COOLDOWN_MS = 15_000; // prevent reload loops — at most once per 15 seconds

function canReload(): boolean {
  try {
    const ts = sessionStorage.getItem(RELOAD_KEY);
    if (!ts) return true;
    return Date.now() - Number(ts) > COOLDOWN_MS;
  } catch {
    return false;
  }
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("error loading dynamically imported module") ||
    error.message.includes("Importing a module script failed")
  );
}

/**
 * Drop-in replacement for React.lazy() that silently reloads the page when a
 * chunk fails to load after a new deployment. The reload happens inside the
 * factory — before React sees an error — so no error boundary or error UI is
 * ever shown to the user. They just see the normal loading spinner.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (isChunkLoadError(error) && canReload()) {
        try {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        } catch {
          // ignore storage errors in restricted environments
        }
        window.location.reload();
        // Return a no-op component — the page will reload before this renders.
        return { default: (() => null) as unknown as T };
      }
      throw error;
    }
  });
}
